import React, { useState, useEffect } from "react";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { Check, Plus, MapPin, Home, X, User, Phone, ChevronDown } from "lucide-react";
import { DeliveryAddress } from "@nusrif/types";
import { trackEvent } from "@nusrif/services";

interface DeliveryAddressBottomSheetProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (address: DeliveryAddress) => void;
  existingAddress?: DeliveryAddress;
}
export const DeliveryAddressBottomSheet = ({
  isOpen,
  onClose,
  onSave,
  existingAddress,
}: DeliveryAddressBottomSheetProps) => {
  const [mode, setMode] = useState<"select" | "add" | "edit">("select");
  const [savedAddresses, setSavedAddresses] = useState<DeliveryAddress[]>([]);
  const [selectedAddressId, setSelectedAddressId] = useState<string>("");

  const [formData, setFormData] = useState<Partial<DeliveryAddress>>({
    name: "",
    phone: "",
    addressLine1: "",
    addressLine2: "",
    city: "",
    state: "",
    pincode: "",
    type: "home",
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isLoading, setIsLoading] = useState(false);
  // Reset form data when existingAddress changes or modal opens
  useEffect(() => {
    if (isOpen) {
      if (existingAddress) {
        // Pre-fill form with existing address data for editing
        setFormData({
          name: existingAddress.name,
          phone: existingAddress.phone,
          addressLine1: existingAddress.addressLine1,
          addressLine2: existingAddress.addressLine2 || "",
          city: existingAddress.city,
          state: existingAddress.state,
          pincode: existingAddress.pincode,
          type: existingAddress.type,
        });
        setMode("edit");
      } else {
        // Reset form for new address
        setFormData({
          name: "",
          phone: "",
          addressLine1: "",
          addressLine2: "",
          city: "",
          state: "",
          pincode: "",
          type: "home",
        });
      }
    }
  }, [isOpen, existingAddress]);
  // Load saved addresses on mount
  useEffect(() => {
    if (isOpen) {
      const savedAddressesData = localStorage.getItem("shippingAddresses");
      if (savedAddressesData) {
        try {
          const addresses = JSON.parse(savedAddressesData);
          setSavedAddresses(addresses);

          // If we have existing address, set edit mode
          if (existingAddress) {
            setMode("edit");
          } else if (addresses.length > 0) {
            setMode("select");
          } else {
            setMode("add");
          }
        } catch (error) {
          console.error("Error loading addresses:", error);
          setMode("add");
        }
      } else {
        setMode("add");
      }
    }
  }, [isOpen, existingAddress]);
  const handleInputChange = (field: keyof DeliveryAddress, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };
  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};
    if (!formData.name?.trim()) newErrors.name = "Name is required";
    if (!formData.phone?.trim()) newErrors.phone = "Phone number is required";
    else if (!/^\d{10}$/.test(formData.phone.replace(/\D/g, ""))) {
      newErrors.phone = "Please enter a valid 10-digit phone number";
    }
    if (!formData.addressLine1?.trim())
      newErrors.addressLine1 = "Address is required";
    if (!formData.city?.trim()) newErrors.city = "City is required";
    if (!formData.state?.trim()) newErrors.state = "State is required";
    if (!formData.pincode?.trim()) newErrors.pincode = "Pincode is required";
    else if (!/^\d{6}$/.test(formData.pincode)) {
      newErrors.pincode = "Please enter a valid 6-digit pincode";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  const handleSelectAddress = () => {
    if (!selectedAddressId) return;

    const selectedAddress = savedAddresses.find(
      (addr) => addr.id === selectedAddressId
    );
    if (selectedAddress) {
      // Track address selection
      trackEvent({
        event: "address_selected",
        properties: {
          address_id: selectedAddress.id,
          address_type: selectedAddress.type,
          city: selectedAddress.city,
          state: selectedAddress.state,
          pincode: selectedAddress.pincode,
          total_addresses: savedAddresses.length
        }
      });
      
      onSave(selectedAddress);
      onClose();
    }
  };
  const handleSaveNewAddress = async () => {
    if (!validateForm()) return;
    setIsLoading(true);
    try {
      const address: DeliveryAddress = {
        id: existingAddress?.id || Date.now().toString(),
        name: formData.name!,
        phone: formData.phone!,
        addressLine1: formData.addressLine1!,
        addressLine2: formData.addressLine2,
        city: formData.city!,
        state: formData.state!,
        pincode: formData.pincode!,
        type: formData.type!,
      };
      // Save to localStorage for future use
      const updatedAddresses = existingAddress
        ? savedAddresses.map((addr) =>
            addr.id === existingAddress.id ? address : addr
          )
        : [...savedAddresses, address];

      localStorage.setItem(
        "shippingAddresses",
        JSON.stringify(updatedAddresses)
      );
      await new Promise((resolve) => setTimeout(resolve, 500)); // Simulate API call
      
      // Track address operation
      trackEvent({
        event: existingAddress ? "address_updated" : "address_added",
        properties: {
          address_id: address.id,
          address_type: address.type,
          city: address.city,
          state: address.state,
          pincode: address.pincode,
          has_address_line2: !!address.addressLine2,
          total_addresses: updatedAddresses.length,
          operation_type: existingAddress ? "edit" : "add"
        }
      });
      
      onSave(address);
      onClose();
    } catch (error) {
      console.error("Error saving address:", error);
    } finally {
      setIsLoading(false);
    }
  };
  const formatAddress = (address: DeliveryAddress): string => {
    const parts = [
      address.addressLine1,
      address.addressLine2,
      address.city,
      address.state,
      address.pincode,
    ].filter(Boolean);
    return parts.join(", ");
  };
  const getAddressTypeLabel = (type: string): string => {
    switch (type) {
      case "home":
        return "Home";
      case "office":
        return "Office";
      default:
        return "Other";
    }
  };
  const getAddressTypeColor = (type: string): string => {
    switch (type) {
      case "home":
        return "bg-blue-50 text-blue-700";
      case "office":
        return "bg-green-50 text-green-700";
      default:
        return "bg-gray-50 text-gray-700";
    }
  };
  const addressTypes = [
    { value: "home", label: "Home", icon: Home },
    { value: "office", label: "Office", icon: MapPin },
    { value: "other", label: "Other", icon: MapPin },
  ];
  const states = [
    "Andhra Pradesh",
    "Arunachal Pradesh",
    "Assam",
    "Bihar",
    "Chhattisgarh",
    "Goa",
    "Gujarat",
    "Haryana",
    "Himachal Pradesh",
    "Jammu and Kashmir",
    "Jharkhand",
    "Karnataka",
    "Kerala",
    "Madhya Pradesh",
    "Maharashtra",
    "Manipur",
    "Meghalaya",
    "Mizoram",
    "Nagaland",
    "Odisha",
    "Punjab",
    "Rajasthan",
    "Sikkim",
    "Tamil Nadu",
    "Telangana",
    "Tripura",
    "Uttarakhand",
    "Uttar Pradesh",
    "West Bengal",
    "Andaman and Nicobar Islands",
    "Chandigarh",
    "Dadra and Nagar Haveli",
    "Daman and Diu",
    "Delhi",
    "Lakshadweep",
    "Puducherry"
  ];
  if (!isOpen) return <></>;
  return (
    <div className="fixed inset-0 z-[9999]">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50 transition-opacity duration-300"
        onClick={onClose}
      />

      {/* Bottom Sheet */}
      <div
        className={`absolute bottom-0 left-0 right-0 bg-white rounded-t-3xl transform transition-transform duration-300 ${
          isOpen ? "translate-y-0" : "translate-y-full"
        }`}
      >
        <div className="max-h-[90vh] overflow-y-auto">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200">
            <h2 className="text-xl font-bold text-black">
              {mode === "select"
                ? "Select Address"
                : mode === "edit"
                  ? "Edit Address"
                  : "Add Delivery Address"}
            </h2>
            <Button
              variant="ghost"
              size="sm"
              className="w-8 h-8 p-0"
              onClick={onClose}
            >
              <X className="w-5 h-5" />
            </Button>
          </div>
          {/* Content */}
          <div className="p-6">
            {mode === "select" && (
              <div className="space-y-4">
                {/* Saved Addresses */}
                <div className="space-y-3">
                  {savedAddresses.map((address) => (
                    <div
                      key={address.id}
                      className={`p-4 border-2 rounded-lg cursor-pointer transition-all duration-200 ${
                        selectedAddressId === address.id
                          ? "border-primary-blue bg-blue-50"
                          : "border-gray-200 hover:border-gray-300"
                      }`}
                      onClick={() => setSelectedAddressId(address.id)}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex items-start gap-3 flex-1">
                          <MapPin className="w-5 h-5 text-gray-600 mt-0.5" />
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="font-medium text-black">
                                {address.name}
                              </span>
                              {address.type && (
                                <span
                                  className={`text-xs px-2 py-1 rounded-full ${getAddressTypeColor(address.type)}`}
                                >
                                  {getAddressTypeLabel(address.type)}
                                </span>
                              )}
                            </div>
                            <p className="text-sm text-gray-600 mb-1">
                              {address.phone}
                            </p>
                            <p className="text-sm text-gray-700 leading-relaxed">
                              {formatAddress(address)}
                            </p>
                          </div>
                        </div>

                        {/* Selection Indicator */}
                        <div
                          className={`w-5 h-5 rounded-full border-2 transition-colors flex items-center justify-center ${
                            selectedAddressId === address.id
                              ? "border-primary-blue bg-primary-blue"
                              : "border-gray-300"
                          }`}
                        >
                          {selectedAddressId === address.id && (
                            <Check className="w-3 h-3 text-white" />
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                {/* Add New Address Button */}
                <Button
                  onClick={() => setMode("add")}
                  variant="outline"
                  className="w-full h-12 border-dashed border-gray-300 text-gray-600 hover:border-gray-400 hover:text-gray-700 flex items-center gap-2"
                >
                  <Plus className="w-4 h-4" />
                  Add New Address
                </Button>
                {/* Select Address Button */}
                <Button
                  onClick={handleSelectAddress}
                  disabled={!selectedAddressId}
                  className="w-full h-14 text-lg font-semibold rounded-full"
                >
                  Use Selected Address
                </Button>
              </div>
            )}
            {(mode === "add" || mode === "edit") && (
              <div className="space-y-6">
                {/* Back to Select Button (only show if we have saved addresses and not editing) */}
                {mode === "add" && savedAddresses.length > 0 && (
                  <Button
                    onClick={() => setMode("select")}
                    variant="outline"
                    className="w-full h-10 text-gray-600 border-gray-300"
                  >
                    ← Back to Saved Addresses
                  </Button>
                )}
                {/* Contact Information */}
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-black">
                    Contact Information
                  </h3>

                  <div className="space-y-2">
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <Input
                        type="text"
                        placeholder="Full Name"
                        value={formData.name}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                          handleInputChange("name", e.target.value)
                        }
                        className={`pl-10 h-12 ${errors.name ? "border-red-500" : "border-gray-500"}`}
                      />
                    </div>
                    {errors.name && (
                      <p className="text-sm text-red-500">{errors.name}</p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <div className="relative">
                      <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <Input
                        type="tel"
                        placeholder="Phone Number"
                        value={formData.phone}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                          handleInputChange("phone", e.target.value)
                        }
                        className={`pl-10 h-12 ${errors.phone ? "border-red-500" : "border-gray-500"}`}
                      />
                    </div>
                    {errors.phone && (
                      <p className="text-sm text-red-500">{errors.phone}</p>
                    )}
                  </div>
                </div>
                {/* Address Information */}
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-black">
                    Address Information
                  </h3>

                  <div className="space-y-2">
                    <Input
                      type="text"
                      placeholder="Address Line 1"
                      value={formData.addressLine1}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        handleInputChange("addressLine1", e.target.value)
                      }
                      className={`h-12 ${errors.addressLine1 ? "border-red-500" : "border-gray-500"}`}
                    />
                    {errors.addressLine1 && (
                      <p className="text-sm text-red-500">
                        {errors.addressLine1}
                      </p>
                    )}
                  </div>
                  <Input
                    type="text"
                    placeholder="Address Line 2 (Optional)"
                    value={formData.addressLine2}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      handleInputChange("addressLine2", e.target.value)
                    }
                    className="h-12 border-gray-500"
                  />
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Input
                        type="text"
                        placeholder="City"
                        value={formData.city}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                          handleInputChange("city", e.target.value)
                        }
                        className={`h-12 ${errors.city ? "border-red-500" : "border-gray-500"}`}
                      />
                      {errors.city && (
                        <p className="text-sm text-red-500">{errors.city}</p>
                      )}
                    </div>
                    <div className="relative">
                      <select
                        value={formData.state}
                        onChange={(e) => handleInputChange("state", e.target.value)}
                        className={`appearance-none h-12 w-full rounded-lg border bg-white px-3 pr-10 py-2 text-base shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-blue transition-all ${errors.state ? "border-red-500" : "border-gray-300 focus:border-primary-blue"} hover:border-primary-blue`}
                        aria-invalid={!!errors.state}
                      >
                        <option value="">Select State</option>
                        {states.map((state) => (
                          <option key={state} value={state}>
                            {state}
                          </option>
                        ))}
                      </select>
                      <div className="pointer-events-none absolute inset-y-0 right-3 flex items-center">
                        <ChevronDown className="w-5 h-5 text-gray-400" />
                      </div>
                      {errors.state && (
                        <p className="text-sm text-red-500">{errors.state}</p>
                      )}
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Input
                      type="text"
                      placeholder="Pincode"
                      value={formData.pincode}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        handleInputChange("pincode", e.target.value)
                      }
                      className={`h-12 ${errors.pincode ? "border-red-500" : "border-gray-500"}`}
                      maxLength={6}
                    />
                    {errors.pincode && (
                      <p className="text-sm text-red-500">{errors.pincode}</p>
                    )}
                  </div>
                </div>
                {/* Address Type */}
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-black">
                    Address Type
                  </h3>
                  <div className="flex gap-3">
                    {addressTypes.map((type) => {
                      const Icon = type.icon;
                      return (
                        <Button
                          key={type.value}
                          variant={
                            formData.type === type.value ? "default" : "outline"
                          }
                          className="flex-1 h-12"
                          onClick={() =>
                            handleInputChange(
                              "type",
                              type.value as "home" | "office" | "other"
                            )
                          }
                        >
                          <Icon className="w-4 h-4 mr-2" />
                          {type.label}
                        </Button>
                      );
                    })}
                  </div>
                </div>
                {/* Save Button */}
                <Button
                  onClick={handleSaveNewAddress}
                  disabled={isLoading}
                  className="w-full h-14 text-lg font-semibold rounded-full"
                >
                  {isLoading ? (
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : mode === "edit" ? (
                    "Update Address"
                  ) : (
                    "Save Address"
                  )}
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
